import { useFeatureFlagEnabled } from 'posthog-js/react';

import type { PostHogFeatureFlag } from '@/config/availableFeatureFlags';
import { useFeatureFlagsContext } from '@/contexts/FeatureFlagsContext';

interface PostHogFeatureFlagResult {
    enabled: boolean;
    isLoading: boolean;
}

/**
 * Checks a feature flag against PostHog (production) with local dev override fallback.
 *
 * Evaluation order:
 * 1. Local dev override (non-production only) — if enabled locally, returns true immediately.
 * 2. PostHog remote evaluation — waits for flags to load, then returns the remote value.
 *
 * While PostHog flags are loading, `isLoading` is true and `enabled` is false.
 */
export function usePostHogFeatureFlag(flagKey: PostHogFeatureFlag): PostHogFeatureFlagResult {
    const { hasFeature, isNonProduction } = useFeatureFlagsContext();
    const localOverride = isNonProduction && hasFeature(flagKey);

    const remoteEnabled = useFeatureFlagEnabled(flagKey);

    if (localOverride) {
        return { enabled: true, isLoading: false };
    }

    return {
        enabled: remoteEnabled ?? false,
        isLoading: remoteEnabled === undefined,
    };
}
